import { database } from "./database";
import { Collections } from "@/types/Collections.ts";
import {
  Appointment,
  CreateAppointmentDto,
  PaginationOptions,
  TypeSenseAppointment,
} from "@/models";
import { SortingState } from "@tanstack/react-table";
import { SearchParams } from "typesense/lib/Typesense/Documents";
import { typesense } from "@/services/typesense";
import { config } from "@/lib/config";

export const appointment = {
  get: (id: string, tenantId: string) => {
    return database.get<Appointment>(
      id,
      `${Collections.tenants}/${tenantId}/${Collections.appointments}`
    );
  },
  create: async (appointmentData: CreateAppointmentDto, tenantId: string) => {
    const newAppointment: CreateAppointmentDto = {
      ...appointmentData,
    };
    return database.create<Appointment>(
      newAppointment,
      `${Collections.tenants}/${tenantId}/${Collections.appointments}`
    );
  },
  update: async (
    appointmentId: string,
    data: Partial<Appointment>,
    tenantId: string
  ) => {
    return database.update<Appointment>(
      { id: appointmentId, ...data },
      `${Collections.tenants}/${tenantId}/${Collections.appointments}/${appointmentId}`
    );
  },
  getAll: async (tenantId: string) => {
    return database.list(
      `${Collections.tenants}/${tenantId}/${Collections.appointments}`
    );
  },
  list: async (
    tenantId: string,
    paginationOptions: PaginationOptions<Appointment>
  ) => {
    return database.paginate(
      `${Collections.tenants}/${tenantId}/${Collections.appointments}`,
      {
        orderBy: "createdAt",
        ...paginationOptions,
      }
    );
  },
  subscribe: (
    appointmentId: string,
    callback: (doc: Appointment | undefined) => void,
    tenantId: string
  ) => {
    return database.subscribeDocument<Appointment>(
      appointmentId,
      `${Collections.tenants}/${tenantId}/${Collections.appointments}`,
      callback
    );
  },

  convertTypesenseToAppointment: (doc: TypeSenseAppointment): Appointment => {
    return {
      tenantId: doc.tenantId,
      generatorId: doc.generatorId,
      customerId: doc.customerId,
      technicianId: doc.technicianId,
      desiredDate: new Date(doc.desiredDate * 1000),
      automaticallyReschedule: doc.automaticallyReschedule,
      status: doc.status,
      notes: doc.notes,
      startTime: new Date(doc.startTime * 1000),
      endTime: new Date(doc.endTime * 1000),
      _deleted: doc._deleted,
      updatedAt: new Date(doc.updatedAt * 1000),
      createdAt: new Date(doc.createdAt * 1000),
      updatedBy: doc.updatedBy,
      createdBy: doc.createdBy,
      id: doc.id,
      _source: doc._source,
    };
  },
  search: async (
    tenantId: string,
    searchString: string,
    page: number = 1,
    perPage: number = 10,
    orderBy?: SortingState
  ): Promise<{ data: Appointment[]; count: number; total: number }> => {
    const searchParameters: SearchParams = {
      q: searchString,
      query_by: "status",
      filter_by: `tenantId:=${tenantId}`,
      sort_by: orderBy
        ? orderBy
            .map((item) => `${item.id}:${item.desc ? "desc" : "asc"}`)
            .join(",")
        : "",
      infix: "fallback",
      page,
      per_page: perPage,
    };
    const hits = await typesense
      .collections<TypeSenseAppointment>(
        config.typesense.collections.appointment
      )
      .documents()
      .search(searchParameters);
    return {
      total: hits.found ?? 0,
      count: hits.hits?.length ?? 0,
      data:
        hits.hits?.map((d) =>
          appointment.convertTypesenseToAppointment(d.document)
        ) ?? [],
    };
  },
  generateRandom: async (tenantId: string) => {
    if (config.devMode) {
      const { faker } = await import("@faker-js/faker");

      const fakeData: CreateAppointmentDto = {
        desiredDate: faker.date.future(),
        tenantId: tenantId,
        generatorId: "",
        customerId: "",
        status: faker.helpers.arrayElement([
          "scheduled",
          "cancelled",
          "completed",
          "inprogress",
        ]),
        technicianId: "",
        notes: faker.lorem.sentence(),
        automaticallyReschedule: faker.datatype.boolean(),
        partsUsed: faker.lorem.words(),
        serviceType: faker.commerce.department(),
      };

      return await appointment.create(fakeData, tenantId);
    }
  },
};
